<<template>
  <div class="business-page">
    <!-- Loading Overlay -->
    <v-fade-transition>
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-content">
          <div class="loading-logo">
            <v-icon large color="white" class="pulse-logo">mdi-wallet</v-icon>
          </div>
          <div class="loading-text">Loading your dashboard...</div>
          <v-progress-linear indeterminate color="white" height="4" class="loading-bar"></v-progress-linear>
        </div>
      </div>
    </v-fade-transition>

    <!-- Toast Notification System -->
    <transition-group name="toast" tag="div" class="toast-container">
      <div v-for="toast in toasts" :key="toast.id" :class="['toast-item', `toast--${toast.type}`]" @click="removeToast(toast.id)">
        <v-icon small class="toast-icon">{{ toast.icon }}</v-icon>
        <span class="toast-message">{{ toast.message }}</span>
      </div>
    </transition-group>

    <!-- Confetti Canvas -->
    <canvas v-show="showConfetti" ref="confettiCanvas" class="confetti-canvas"></canvas>

    <!-- ═══════════════════════════════════════════════════════
         CREDIT SCORE DIALOG — Redesigné avec glassmorphism
         ═══════════════════════════════════════════════════════ -->
    <v-dialog v-model="showCreditScoreDialog" max-width="800px" content-class="credit-dialog">
      <v-card class="glass-card" style="border-radius: 20px; overflow: hidden;">
        <v-card-title class="dialog-header white--text">
          <div class="dialog-header-content">
            <v-icon left color="white" size="28">mdi-star-circle</v-icon>
            <span class="headline font-weight-bold">Understanding Your Credit Score</span>
          </div>
          <v-btn icon dark @click="showCreditScoreDialog = false" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6" style="max-height: 600px; overflow-y: auto;">
          <div class="credit-score-explanation">
            <!-- Score Circle Animation -->
            <div class="score-circle-container mb-6">
              <div class="score-circle">
                <svg viewBox="0 0 120 120" class="score-svg">
                  <defs>
                    <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style="stop-color:#42A5F5;stop-opacity:1" />
                      <stop offset="100%" style="stop-color:#1565C0;stop-opacity:1" />
                    </linearGradient>
                  </defs>
                  <circle class="score-track" cx="60" cy="60" r="54" />
                  <circle
                    class="score-progress"
                    cx="60" cy="60" r="54"
                    :style="{ strokeDashoffset: 339.292 - (339.292 * Math.min(userCreditScore, 100) / 100) }"
                  />
                </svg>
                <div class="score-value">
                  <span class="score-number">{{ userCreditScore }}</span>
                  <span class="score-label">pts</span>
                </div>
              </div>
            </div>

            <div class="mb-5">
              <h3 class="blue--text text--darken-2 mb-3 section-heading">
                <span class="emoji-icon">🌟</span> What is a Credit Score?
              </h3>
              <p class="body-1 grey--text text--darken-2">
                Your <strong class="blue--text text--darken-2">Credit Score</strong> serves as a tangible digital guarantee within the PettyCash ecosystem.
                It enables Business Suite clients to access credit facilities from PettyCash SAS, specifically in the form
                of goods and services, using their score as leverage rather than traditional cash deposits.
              </p>
              <v-alert
                color="blue lighten-5"
                border="left"
                colored-border
                type="info"
                elevation="0"
                class="mt-4 info-alert"
              >
                <strong class="blue--text text--darken-2">💡 Key Concept:</strong> Your Credit Score represents your financial reliability and unlocks
                opportunities without requiring upfront cash deposits. The higher your score, the more financial
                flexibility you gain.
              </v-alert>
            </div>

            <div class="mb-5">
              <h3 class="blue--text text--darken-2 mb-3 section-heading">
                <span class="emoji-icon">📈</span> Credit Accumulation Protocol
              </h3>
              <p class="body-1 grey--text text--darken-2 mb-4">
                Your Credit Score grows through two distinct channels: <strong>Personal Status</strong> (your individual actions)
                and <strong>Network Engagement</strong> (activities of your referred users).
              </p>

              <v-card class="mb-4 accumulation-card" elevation="2">
                <v-card-title class="py-3 px-4 blue lighten-5">
                  <v-icon small color="blue darken-2" class="mr-2">mdi-account-star</v-icon>
                  <span class="subtitle-2 font-weight-bold blue--text text--darken-2">Personal Status Points</span>
                </v-card-title>
                <v-card-text class="pa-0">
                  <v-list dense>
                    <v-list-item v-for="(item, i) in personalPoints" :key="i" class="point-item">
                      <v-list-item-icon class="mr-3">
                        <v-icon small color="green">mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" v-html="item"></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>

              <v-card class="mb-4 accumulation-card" elevation="2">
                <v-card-title class="py-3 px-4 orange lighten-5">
                  <v-icon small color="orange darken-2" class="mr-2">mdi-account-group</v-icon>
                  <span class="subtitle-2 font-weight-bold orange--text text--darken-2">Network Engagement Points</span>
                </v-card-title>
                <v-card-text class="pa-0">
                  <v-list dense>
                    <v-list-item v-for="(item, i) in networkPoints" :key="i" class="point-item">
                      <v-list-item-icon class="mr-3">
                        <v-icon small color="orange">mdi-star-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" v-html="item"></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>

              <v-alert
                color="orange lighten-5"
                border="left"
                colored-border
                type="warning"
                elevation="0"
                class="mt-4 info-alert"
              >
                <strong class="orange--text text--darken-2">🎯 Pro Tip:</strong> Build your network strategically! Each active referred user multiplies
                your earning potential through their ongoing activities.
              </v-alert>
            </div>

            <div class="mb-4">
              <h3 class="blue--text text--darken-2 mb-3 section-heading">
                <span class="emoji-icon">💎</span> Benefits of a Good Credit Score
              </h3>
              <div class="benefits-grid">
                <div v-for="(benefit, i) in benefits" :key="i" class="benefit-item" :style="{ animationDelay: `${i * 0.1}s` }">
                  <v-icon small color="blue darken-2" class="mr-2">{{ benefit.icon }}</v-icon>
                  <span class="caption font-weight-medium">{{ benefit.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-5 dialog-actions">
          <v-btn
            color="blue darken-2"
            dark
            @click="showCreditScoreDialog = false"
            class="got-it-btn"
            large
            rounded
          >
            <v-icon left>mdi-check</v-icon>
            Got it!
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Hero Section - COMPACT ET SOIGNÉ -->
    <div class="hero-section">
      <div class="hero-gradient"></div>
      <div class="hero-pattern"></div>
      <div class="hero-particles" ref="particlesContainer"></div>

      <v-container class="hero-content">
        <!-- Welcome Badge -->
        <div class="welcome-badge" :class="{ 'animate-in': !isLoading }" v-if="$store.state.isAuthenticated">
          <span class="badge-icon wave">👋</span>
          <span class="badge-text">
           Welcome back, <strong class="notranslate username-text" translate="no">{{ displayUsername }}</strong>!
          </span>
          <span class="badge-time"></span>
        </div>
        <div class="welcome-badge" :class="{ 'animate-in': !isLoading }" v-else>
          <span class="badge-icon">🚀</span>
          <span class="badge-text">Welcome to <strong>PettyCash Business Suite</strong></span>
        </div>

        <!-- Credit Score Badge -->
        <div class="credit-score-badge" @click="showCreditScoreDialog = true" :class="{ 'animate-in': !isLoading }">
          <div class="credit-pulse"></div>
          <v-icon small class="credit-icon">mdi-star-circle</v-icon>
          <span class="credit-text">Credit Score: <strong class="credit-value">{{ userCreditScore }}</strong></span>
          <div class="credit-level" :class="creditLevelClass">{{ creditLevel }}</div>
          <v-icon small class="info-icon">mdi-information-outline</v-icon>
        </div>

        <!-- Stats Summary -->
        <div class="stats-summary" :class="{ 'animate-in': !isLoading }">
          <div v-for="(stat, index) in stats" :key="stat.key" class="stat-item"
            @mouseenter="stat.hovered = true" @mouseleave="stat.hovered = false"
            :class="{ 'stat-hovered': stat.hovered }" :style="{ animationDelay: (0.3 + index * 0.1) + 's' }">
            <div class="stat-info">
              <span class="stat-label">{{ stat.label }}</span>
              <span class="stat-value" :class="{ 'count-up': stat.animated }">{{ stat.displayValue }}</span>
            </div>
          </div>
        </div>

        <!-- Referral Share Section -->
        <div class="referral-share" :class="{ 'animate-in': !isLoading }">
          <div class="referral-header">
            <div class="referral-label">
              <v-icon x-small class="mr-1" style="color: rgba(255,255,255,0.7)">mdi-link-variant</v-icon>
              Your Referral Link
            </div>
            <div class="referral-reward">
              <v-icon x-small color="#42A5F5">mdi-gift</v-icon>
              <span>Earn +5 pts</span>
            </div>
          </div>

          <div class="referral-link-preview" @click="copyReferralLink" title="Click to copy">
            <span class="referral-link-text">{{ truncatedReferralLink }}</span>
            <button class="copy-inline-btn" @click.stop="copyReferralLink" title="Copy Link">
              <v-icon x-small color="white">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </button>
          </div>

          <div class="referral-code-row">
            <div class="referral-code-chip notranslate" translate="no" @click="copyCode">
              <v-icon x-small color="#42A5F5" class="mr-1">mdi-tag</v-icon>
              {{ $store.state.user_info?.user_profile?.serial_no || 'N/A' }}
              <v-icon x-small color="rgba(255,255,255,0.5)" class="ml-1 copy-hint">mdi-content-copy</v-icon>
            </div>
            <span class="referral-code-hint">Referral Code</span>
          </div>

          <div class="share-buttons-row">
            <button v-for="btn in shareButtons" :key="btn.name" class="share-pill" :class="'share-pill--' + btn.type" @click="btn.action" :title="btn.label">
              <i :class="btn.icon"></i>
              <span class="pill-label">{{ btn.label }}</span>
            </button>
          </div>
        </div>
      </v-container>
    </div>

    <!-- Main Content -->
    <v-container class="main-content">
      <v-layout row>
        <v-flex xs12 md10 offset-md1 lg10 offset-lg1>
          <v-card flat class="card--flex-toolbar" color="transparent">
            <v-container fluid grid-list-lg>
              <!-- Section Header - SIMPLIFIÉ -->
              <v-layout row wrap>
                <v-flex xs12 class="section-header-flex">
                  <div @click="$router.push('/business-account/dashboard/hub')" style="cursor: pointer; display: inline-block;">
                    <h3 class="section-title">Business Suite Services</h3>
                    <p class="section-subtitle">Access your tools and manage your business</p>
                  </div>
                </v-flex>
              </v-layout>

              <!-- Cards Grid -->
              <v-layout row wrap>
                <v-flex xs12 sm6 md6 lg4 v-for="(item, index) in visibleLinks" :key="item.id" class="pa-2 card-wrapper" :style="{ animationDelay: (index * 0.1) + 's' }">
                  <v-hover v-slot="{ hover }">
                    <v-card :class="['service-card', { 'elevated': hover }]" :elevation="hover ? 16 : 2" :to="item.link">
                      <div class="card-shine" v-if="hover"></div>
                      <div class="card-accent-border" :style="{ background: getAccentColor(item.color) }"></div>
                      <div class="card-gradient" :style="{ background: getCardGradient(item.color) }"></div>

                      <v-layout row align-start>
                        <v-flex xs8 sm8 md8>
                          <v-btn fab :color="item.color" class="icon-btn white--text" :class="{ 'icon-pulse': hover }">
                            <i :class="item.icon" class="icon-display"></i>
                          </v-btn>
                        </v-flex>

                        <v-flex xs4 sm4 md4 v-if="!(item.title == 'Legal Bindings')" class="text-xs-right">
                          <v-menu bottom right>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                flat
                                fab
                                class="menu-btn mt-2 ml-3 mb-0"
                                v-on="on"
                                @click.prevent="activateMenu(item.title)"
                              >
                                <i class="fa-solid fa-ellipsis-vertical fa-2x"></i>
                              </v-btn>
                            </template>
                            <v-list class="grey lighten-4 menu-list">
                              <v-list-tile
                                v-for="menuLink in menuItems"
                                :key="menuLink.title"
                                :to="menuLink.link"
                                class="menu-item"
                              >
                                <v-list-tile-title>{{ menuLink.title }}</v-list-tile-title>
                              </v-list-tile>
                            </v-list>
                          </v-menu>
                        </v-flex>
                      </v-layout>

                      <v-card-title primary-title class="card-title-area">
                        <div class="card-content">
                          <h5 class="card-title">
                            <b>{{ item.title }}</b>
                            <v-chip v-if="item.badge" x-small :color="item.badgeColor || 'blue darken-2'" text-color="white" class="ml-2 card-badge">{{ item.badge }}</v-chip>
                          </h5>
                          <div class="card-description">{{ item.description }}</div>
                          <div class="card-footer">
                            <span class="card-link">
                              {{ item.action || 'Explore' }}
                              <v-icon small class="card-arrow-icon">mdi-arrow-right</v-icon>
                            </span>
                          </div>
                        </div>
                      </v-card-title>
                    </v-card>
                  </v-hover>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Back to Top Button -->
    <v-fab-transition>
      <v-btn v-show="showBackToTop" fixed bottom right color="blue darken-2" dark fab class="back-to-top" @click="scrollToTop">
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
    </v-fab-transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BusinessDashboard',

  data() {
    return {
      isLoading: true,
      showCreditScoreDialog: false,
      showBackToTop: false,
      showConfetti: false,
      copied: false,

      userBalance: 0,
      pendingCommissions: 0,
      total_commissions: 0,
      userCreditScore: 0,
      affiliateCount: 0,
      isSeller: false,
      encryptedReferralCode: '',
      greeting: '',

      toasts: [],
      toastId: 0,

      stats: [
        { key: 'balance', label: 'Balance', value: 0, displayValue: '0 FCFA', animated: false, hovered: false },
        { key: 'commissions', label: 'Commissions', value: 0, displayValue: '0 FCFA', animated: false, hovered: false },
        { key: 'sales', label: 'Total Sales', value: 0, displayValue: '0', animated: false, hovered: false },
      ],

      links: [
        {
          id: "1",
          title: "Pettycash Basic Services",
          description: "Access essential services for managing petty cash and daily transactions.",
          color: "teal",
          icon: "fa-solid fa-wallet fa-2x",
          link: "/All",
          add_link: false,
          sellerOnly: false,
          action: "Access Services",
          badge: "Popular",
          badgeColor: "teal",
        },
        {
          id: "hub",
          title: "My Dashboard",
          description: "Access all your tools: affiliates, commissions, groups, vendor space, and more.",
          color: "indigo",
          icon: "fa-solid fa-wallet fa-2x",
          link: "/business-account/dashboard/hub",
          add_link: false,
          sellerOnly: false,
          action: "Open Hub",
        },
      ],

      menuItems: [],

      // Données pour le dialogue de score de crédit
      personalPoints: [
        'Business Account Validation: <strong>+10 points</strong> when approved',
        'On-time Campaign Completion: Bonus points based on performance'
      ],
      networkPoints: [
        'Successful Referral: <strong>+5 points</strong> when a user joins through your link',
        'Referred User Njangi Contribution: <strong>+5 points</strong> per validated contribution',
        'Referred User Campaign Completion: <strong>+5 points</strong> for each completed campaign'
      ],
      benefits: [
        { icon: 'mdi-truck-fast', text: 'Early Delivery at 50%' },
        { icon: 'mdi-shield-check', text: 'Higher Trust Level' },
        { icon: 'mdi-percent', text: 'Better Loan Terms' },
        { icon: 'mdi-star', text: 'Priority Support' }
      ],
    };
  },

  computed: {
    displayUsername() {
      return (
        this.$store?.state?.user_info?.user?.username ||
        this.$store?.state?.username ||
        this.username ||
        ''
      );
    },

    visibleLinks() {
      return this.links.filter(item => {
        if (item.hideWhenSeller && this.isSeller) return false;
        return !item.sellerOnly || this.isSeller;
      });
    },

    truncatedReferralLink() {
      const link = this.generateReferralLink();
      return link.length > 45 ? link.substring(0, 45) + '...' : link;
    },

    canShareNative() {
      return typeof navigator !== 'undefined' && !!navigator.share;
    },

    shareButtons() {
      const buttons = [
        { name: 'copy', type: 'copy', icon: 'fas fa-copy', label: 'Copy', action: () => this.copyReferralLink() },
        { name: 'whatsapp', type: 'whatsapp', icon: 'fab fa-whatsapp', label: 'WhatsApp', action: () => this.shareViaWhatsApp() },
        { name: 'facebook', type: 'facebook', icon: 'fab fa-facebook-f', label: 'Facebook', action: () => this.shareViaFacebook() },
        { name: 'sms', type: 'sms', icon: 'fas fa-sms', label: 'SMS', action: () => this.shareViaSMS() },
      ];
      if (this.canShareNative) {
        buttons.push({ name: 'more', type: 'more', icon: 'fas fa-share-alt', label: 'More', action: () => this.shareNative() });
      }
      return buttons;
    },

    creditLevel() {
      const score = this.userCreditScore;
      if (score >= 100) return 'Elite';
      if (score >= 75) return 'Gold';
      if (score >= 50) return 'Silver';
      if (score >= 25) return 'Bronze';
      return 'Starter';
    },

    creditLevelClass() {
      const score = this.userCreditScore;
      if (score >= 100) return 'level-elite';
      if (score >= 75) return 'level-gold';
      if (score >= 50) return 'level-silver';
      if (score >= 25) return 'level-bronze';
      return 'level-starter';
    },
  },

  mounted() {
    this.setGreeting();
    document.title = "PettyCash | Business Dashboard";

    this.fetchDashboardData();
    this.getUserCreditScore();
    this.checkSellerStatus();
    this.initEncryptedCode();

    setTimeout(() => { this.isLoading = false; this.animateStats(); }, 1200);

    this.initScrollListener();
    this.initKeyboardShortcuts();
    this.initParticles();
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('keydown', this.handleKeydown);
    this.clearParticles();
  },

  async beforeMount() {
    try {
      const response = await axios.get('/api/v1/home/user/profile/');
      if (response.data.payment_status === false) {
        this.$router.push('/');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      this.$router.push('/');
    }
  },

  methods: {
    setGreeting() {
      const hour = new Date().getHours();
      if (hour < 12) this.greeting = 'Good morning ☀️';
      else if (hour < 17) this.greeting = 'Good afternoon 🌤️';
      else if (hour < 21) this.greeting = 'Good evening 🌅';
      else this.greeting = 'Good night 🌙';
    },

    showToast(message, type = 'success', icon = 'mdi-check-circle') {
      const id = ++this.toastId;
      this.toasts.push({ id, message, type, icon });
      setTimeout(() => this.removeToast(id), 4000);
    },

    removeToast(id) {
      this.toasts = this.toasts.filter(t => t.id !== id);
    },

    initScrollListener() {
      this.handleScroll = () => {
        this.showBackToTop = window.scrollY > 400;
      };
      window.addEventListener('scroll', this.handleScroll);
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    initKeyboardShortcuts() {
      this.handleKeydown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
          e.preventDefault();
          this.copyReferralLink();
        }
        if (e.key === 'Escape' && this.showCreditScoreDialog) {
          this.showCreditScoreDialog = false;
        }
      };
      document.addEventListener('keydown', this.handleKeydown);
    },

    initParticles() {
      const container = this.$refs.particlesContainer;
      if (!container) return;

      for (let i = 0; i < 20; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.top = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 5 + 's';
        particle.style.animationDuration = (5 + Math.random() * 10) + 's';
        container.appendChild(particle);
      }
    },

    clearParticles() {
      const container = this.$refs.particlesContainer;
      if (container) container.innerHTML = '';
    },

    animateStats() {
      this.stats.forEach((stat, index) => {
        setTimeout(() => {
          stat.animated = true;
          this.animateValue(stat);
        }, index * 200);
      });
    },

    animateValue(stat) {
      const duration = 1500;
      const start = 0;
      const end = stat.value;
      const startTime = performance.now();

      const update = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(start + (end - start) * easeProgress);

        if (stat.key === 'balance' || stat.key === 'commissions') {
          stat.displayValue = this.formatCurrency(current);
        } else {
          stat.displayValue = current.toLocaleString();
        }

        if (progress < 1) {
          requestAnimationFrame(update);
        }
      };

      requestAnimationFrame(update);
    },

    triggerConfetti() {
      this.showConfetti = true;
      const canvas = this.$refs.confettiCanvas;
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const particles = [];
      const colors = ['#1565C0', '#FFD700', '#FF9800', '#42A5F5', '#9C27B0', '#00C853'];

      for (let i = 0; i < 100; i++) {
        particles.push({
          x: canvas.width / 2,
          y: canvas.height / 2,
          vx: (Math.random() - 0.5) * 20,
          vy: (Math.random() - 0.5) * 20 - 5,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 4,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10,
          opacity: 1,
        });
      }

      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let active = false;

        particles.forEach(p => {
          if (p.opacity > 0) {
            active = true;
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.3;
            p.rotation += p.rotationSpeed;
            p.opacity -= 0.015;

            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate((p.rotation * Math.PI) / 180);
            ctx.globalAlpha = p.opacity;
            ctx.fillStyle = p.color;
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
            ctx.restore();
          }
        });

        if (active) {
          requestAnimationFrame(animate);
        } else {
          this.showConfetti = false;
        }
      };

      requestAnimationFrame(animate);
    },

    refreshData() {
      this.isLoading = true;
      setTimeout(() => {
        this.fetchDashboardData();
        this.getUserCreditScore();
        this.isLoading = false;
        this.showToast('Dashboard refreshed!', 'success', 'mdi-refresh');
      }, 1000);
    },

    encodeReferralCode(serialNo) {
      if (!serialNo) return '';
      try {
        const payload = `pc_ref:${serialNo}`;
        return btoa(payload)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      } catch (e) {
        console.error('Encode error:', e);
        return '';
      }
    },

    initEncryptedCode() {
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no;
      if (serialNo) {
        this.encryptedReferralCode = this.encodeReferralCode(serialNo);
      } else {
        const unwatch = this.$watch(
          () => this.$store.state.user_info?.user_profile?.serial_no,
          (newVal) => {
            if (newVal) {
              this.encryptedReferralCode = this.encodeReferralCode(newVal);
              unwatch();
            }
          },
          { immediate: true }
        );
      }
    },

    generateReferralLink() {
      const baseUrl = "https://myapp.pettycashglobal.com";
      const code = this.encryptedReferralCode
        || this.encodeReferralCode(this.$store.state.user_info?.user_profile?.serial_no);
      return `${baseUrl}/create_business_account2?ref=${code}`;
    },

    copyReferralLink() {
      const link = this.generateReferralLink();
      navigator.clipboard.writeText(link).then(() => {
        this.copied = true;
        this.showToast('Link copied to clipboard!', 'success', 'mdi-content-copy');
        this.triggerConfetti();
        setTimeout(() => { this.copied = false; }, 2000);
      }).catch(() => {
        this.showToast('Failed to copy link', 'error', 'mdi-alert');
      });
    },

    copyCode() {
      const code = this.$store.state.user_info?.user_profile?.serial_no || '';
      if (code) {
        navigator.clipboard.writeText(code).then(() => {
          this.showToast('Code copied!', 'success', 'mdi-tag');
        });
      }
    },

    shareViaWhatsApp() {
      const link = this.generateReferralLink();
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      const message =
        `🌟 Join PettyCash Business Suite!\n\n` +
        `Use my referral code *${serialNo}*:\n${link}\n\n` +
        `_PettyCash — Fintech Made in Cameroon_ 🇨🇲`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
      this.showToast('Opening WhatsApp...', 'info', 'mdi-whatsapp');
    },

    shareViaFacebook() {
      const link = this.generateReferralLink();
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
        '_blank',
        'width=600,height=400'
      );
      this.showToast('Opening Facebook...', 'info', 'mdi-facebook');
    },

    shareViaSMS() {
      const link = this.generateReferralLink();
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      const body = `Join PettyCash with my code ${serialNo}: ${link}`;
      window.open(`sms:?body=${encodeURIComponent(body)}`);
      this.showToast('Opening SMS...', 'info', 'mdi-message');
    },

    async shareNative() {
      if (!navigator.share) return;
      const link = this.generateReferralLink();
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      try {
        await navigator.share({
          title: 'PettyCash Business Suite',
          text: `Join PettyCash with my referral code ${serialNo}`,
          url: link,
        });
        this.showToast('Shared successfully!', 'success', 'mdi-share-variant');
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Share failed:', e);
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF'
      }).format(value || 0);
    },

    async fetchDashboardData() {
      try {
        const response = await axios.get('/api/v1/home/dashboard/');
        const data = response.data;
        this.userBalance = data.balance || 0;
        this.pendingCommissions = data.pending_commissions || 0;
        this.affiliateCount = data.affiliate_count || 0;
        this.total_commissions = data.total_commissions || 0;

        this.stats[0].value = this.userBalance;
        this.stats[1].value = this.total_commissions;
        this.stats[2].value = this.affiliateCount;

        this.referredUsers = (data.referred_users || []).map(user => ({
          id: user.id,
          username: user.username,
          created_at: user.created_at,
          payment_status: user.payment_status,
          payment_amount: user.payment_amount,
          serial_no: user.serial_no
        }));
      } catch (error) {
        console.error('Loading error:', error);
        this.showToast('Failed to load dashboard data', 'error', 'mdi-alert');
      }
    },

    async checkSellerStatus() {
      try {
        const response = await axios.get('/api/v1/market_place/seller/check-status/');
        this.isSeller = response.data.is_seller === true;
      } catch (error) {
        console.error('Error checking seller status:', error);
        this.isSeller = false;
      }
    },

    activateMenu(title) {
      const menus = {
        "Pettycash Basic Services": [
          { title: "Overview", link: "/All" },
          { title: "Transactions", link: "/savings/transactions/all" },

        ],
        "Client of Affiliates": [
          { title: "All Clients", link: "/business-account/dashboard/client-of-affiliates" },
          { title: "Add New Client", link: "/business-account/dashboard/AddUser" },
        ],
        "Affiliate Marketing Tutorial": [
          { title: "Getting Started", link: "/business-account/dashboard/video-tutorial/getting-started" },
          { title: "Advanced Features", link: "/business-account/dashboard/video-tutorial/advanced-features" },
        ]
      };
      this.menuItems = menus[title] || [
        { title: "View Details", link: "/business-account/dashboard/hub" },
   
      ];
    },

    async getUserCreditScore() {
      try {
        const storeCredit = this.$store.state.user_info?.user_credit?.[0]?.credit;
        if (storeCredit !== undefined) {
          this.userCreditScore = storeCredit;
        } else {
          const response = await axios.get('/api/v1/home/get/user/info/');
          this.userCreditScore = response.data?.[0]?.user_credit?.[0]?.credit || 0;
        }
      } catch (error) {
        console.error('Error fetching credit score:', error);
        this.userCreditScore = 0;
      }
    },

    getCardGradient(color) {
      const gradients = {
        orange: 'linear-gradient(135deg, rgba(255, 152, 0, 0.06) 0%, rgba(255, 152, 0, 0) 100%)',
        purple: 'linear-gradient(135deg, rgba(156, 39, 176, 0.06) 0%, rgba(156, 39, 176, 0) 100%)',
        teal: 'linear-gradient(135deg, rgba(21, 101, 192, 0.06) 0%, rgba(21, 101, 192, 0) 100%)',
        blue: 'linear-gradient(135deg, rgba(33, 150, 243, 0.06) 0%, rgba(33, 150, 243, 0) 100%)',
        green: 'linear-gradient(135deg, rgba(76, 175, 80, 0.06) 0%, rgba(76, 175, 80, 0) 100%)',
        indigo: 'linear-gradient(135deg, rgba(63, 81, 181, 0.06) 0%, rgba(63, 81, 181, 0) 100%)',
      };
      return gradients[color] || gradients.blue;
    },

    getAccentColor(color) {
      const accents = {
        orange: '#FF9800',
        purple: '#9C27B0',
        teal: '#1565C0',
        blue: '#2196F3',
        green: '#4CAF50',
        indigo: '#3F51B5',
      };
      return accents[color] || '#2196F3';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.business-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafb 0%, #f0f4f4 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
}

/* ===== LOADING OVERLAY ===== */
.loading-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, #0d47a1 0%, #1565C0 100%);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-content { text-align: center; color: white; }
.loading-logo { margin-bottom: 24px; }
.pulse-logo { font-size: 48px !important; animation: pulse 1.5s ease-in-out infinite; }
.loading-text { font-size: 18px; font-weight: 500; margin-bottom: 24px; opacity: 0.9; }
.loading-bar { max-width: 200px; margin: 0 auto; border-radius: 4px; }

/* ===== TOAST NOTIFICATIONS ===== */
.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.toast-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  cursor: pointer;
  min-width: 280px;
  animation: toastSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid;
}
.toast--success { border-left-color: #00C853; }
.toast--error { border-left-color: #FF5252; }
.toast--info { border-left-color: #2196F3; }
.toast--warning { border-left-color: #FF9800; }
.toast-message { font-size: 14px; font-weight: 500; color: #333; }
.toast-enter-active, .toast-leave-active { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter, .toast-leave-to { opacity: 0; transform: translateX(100%); }

/* ===== CONFETTI CANVAS ===== */
.confetti-canvas {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  z-index: 9998;
}

/* ===== COMPACT HERO SECTION ===== */
.hero-section {
  position: relative;
  padding: 20px 0 35px;
  min-height: auto;
  overflow: hidden;
  background: linear-gradient(135deg, #0d47a1 0%, #1565C0 50%, #1976D2 100%) !important;
}

.hero-gradient {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background:
    radial-gradient(circle at 15% 50%, rgba(66, 165, 245, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 85% 30%, rgba(25, 118, 210, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 50% 80%, rgba(13, 71, 161, 0.08) 0%, transparent 40%);
  pointer-events: none;
}

.hero-pattern {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image:
    radial-gradient(circle at 25px 25px, rgba(255,255,255,0.03) 2px, transparent 0);
  background-size: 50px 50px;
  pointer-events: none;
}

.hero-particles {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: 4px; height: 4px;
  background: rgba(100, 181, 246, 0.4);
  border-radius: 50%;
  animation: floatParticle linear infinite;
}

.hero-content { position: relative; z-index: 1; text-align: center; }

/* ===== COMPACT WELCOME BADGE ===== */
.welcome-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255,255,255,0.2);
  padding: 6px 14px;
  border-radius: 50px;
  color: white;
  font-size: 13px;
  margin-bottom: 10px;
  opacity: 0;
  transform: translateY(-15px);
}

.welcome-badge.animate-in { animation: slideInDown 0.5s ease-out forwards; }

.badge-icon { font-size: 16px; }
.badge-icon.wave { animation: wave 2s ease-in-out infinite; }

.badge-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.badge-text strong { font-weight: 700; }

.badge-time {
  font-size: 10px;
  opacity: 0.7;
  padding-left: 6px;
  border-left: 1px solid rgba(255,255,255,0.2);
  margin-left: 4px;
}

/* ===== COMPACT CREDIT SCORE BADGE ===== */
.credit-score-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(66,165,245,0.2), rgba(30,136,229,0.15));
  backdrop-filter: blur(16px);
  border: 2px solid rgba(100,181,246,0.5);
  padding: 8px 16px;
  border-radius: 50px;
  color: white;
  font-size: 13px;
  margin-bottom: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-15px);
}

.credit-score-badge.animate-in { animation: slideInDown 0.5s ease-out 0.1s forwards; }

.credit-score-badge:hover {
  background: linear-gradient(135deg, rgba(66,165,245,0.3), rgba(30,136,229,0.25));
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 8px 32px rgba(30,136,229,0.3);
}

.credit-pulse {
  position: absolute;
  top: 50%; left: 50%;
  width: 100%; height: 100%;
  border-radius: 50px;
  background: rgba(66,165,245,0.25);
  transform: translate(-50%, -50%);
  animation: creditPulse 2s ease-out infinite;
}

.credit-icon { color: #FFD700; font-size: 20px !important; position: relative; z-index: 1; }
.credit-text { font-weight: 500; position: relative; z-index: 1; }
.credit-value { font-weight: 800; color: #FFD700; font-size: 15px; }

.credit-level {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
}

.level-starter { background: rgba(255,255,255,0.2); color: white; }
.level-bronze { background: #cd7f32; color: white; }
.level-silver { background: #c0c0c0; color: #333; }
.level-gold { background: #FFD700; color: #333; }
.level-elite { background: linear-gradient(135deg, #42A5F5, #1565C0); color: white; }

.info-icon { color: rgba(255,215,0,0.7); font-size: 16px !important; position: relative; z-index: 1; }

/* ===== COMPACT STATS SUMMARY ===== */
.stats-summary {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  opacity: 0;
  transform: translateY(15px);
}

.stats-summary.animate-in { animation: fadeInUp 0.5s ease-out 0.2s forwards; }

.stat-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255,255,255,0.12);
  padding: 10px 16px;
  border-radius: 14px;
  min-width: 140px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
}

.stat-item:hover, .stat-hovered {
  background: rgba(255,255,255,0.15);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(13,71,161,0.15);
}

.stat-info { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; }

.stat-label { font-size: 10px; color: rgba(255,255,255,0.7); text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600; }
.stat-value { font-size: 16px; font-weight: 800; color: white; transition: all 0.3s; }
.stat-value.count-up { color: #42A5F5; }

/* ===== COMPACT REFERRAL SHARE ===== */
.referral-share {
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.12);
  padding: 14px 18px;
  border-radius: 18px;
  max-width: 520px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(15px);
}

.referral-share.animate-in { animation: fadeInUp 0.5s ease-out 0.3s forwards; }

.referral-header { margin-bottom: 8px; }

.referral-label {
  font-size: 10px;
  color: rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 600;
}

.referral-reward {
  font-size: 10px;
  color: #FFD700;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.referral-link-preview {
  display: flex;
  align-items: center;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.referral-link-preview:hover {
  background: rgba(0,0,0,0.35);
  border-color: rgba(100,181,246,0.4);
}

.referral-link-text {
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  color: rgba(255,255,255,0.75);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.3px;
}

.copy-inline-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  transition: all 0.2s;
  border-radius: 6px;
}

.copy-inline-btn:hover { opacity: 1; background: rgba(255,255,255,0.1); }

.referral-code-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.referral-code-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(100,181,246,0.4);
  border-radius: 50px;
  padding: 4px 12px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 2px;
  color: white;
  cursor: pointer;
  transition: all 0.3s;
}

.referral-code-chip:hover {
  background: rgba(66,165,245,0.15);
  transform: scale(1.02);
}

.copy-hint { opacity: 0.5; transition: opacity 0.2s; }
.referral-code-chip:hover .copy-hint { opacity: 1; }

.referral-code-hint {
  font-size: 10px;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 500;
}

.share-buttons-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.share-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  letter-spacing: 0.3px;
  outline: none;
  position: relative;
  overflow: hidden;
}

.share-pill::before {
  content: '';
  position: absolute;
  top: 0; left: -100%;
  width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.share-pill:hover::before { left: 100%; }

.share-pill i { font-size: 14px; }
.share-pill:hover { transform: translateY(-3px); filter: brightness(1.15); }
.share-pill:active { transform: translateY(0) scale(0.96); }

.share-pill--copy {
  background: rgba(255,255,255,0.15);
  color: white;
  border: 1px solid rgba(255,255,255,0.25);
}

.share-pill--copy:hover { background: rgba(255,255,255,0.25); }

.share-pill--whatsapp {
  background: #25D366;
  color: white;
  box-shadow: 0 4px 16px rgba(37,211,102,0.35);
}

.share-pill--facebook {
  background: #1877F2;
  color: white;
  box-shadow: 0 4px 16px rgba(24,119,242,0.35);
}

.share-pill--sms {
  background: #FF9800;
  color: white;
  box-shadow: 0 4px 16px rgba(255,152,0,0.35);
}

.share-pill--more {
  background: rgba(255,255,255,0.12);
  color: white;
  border: 1px solid rgba(255,255,255,0.2);
}

/* ===== MAIN CONTENT - CLOSER TO HERO ===== */
.main-content { margin-top: -60px; position: relative; z-index: 2; }

.card--flex-toolbar { margin-top: 0; }

/* Section Header - SIMPLIFIÉ */
.section-header-flex {
  text-align: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* Service Cards */
.card-wrapper {
  opacity: 0;
  animation: fadeInUp 0.6s ease forwards;
}

.service-card {
  position: relative;
  background: white;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 16px !important;
}

.card-shine {
  position: absolute;
  top: 0; left: -100%;
  width: 50%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shine 0.6s ease-out;
  pointer-events: none;
  z-index: 10;
}

.card-accent-border {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  opacity: 0;
  transition: opacity 0.4s;
}

.service-card:hover .card-accent-border,
.service-card.elevated .card-accent-border {
  opacity: 1;
}

.card-gradient {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  opacity: 0;
  transition: opacity 0.4s;
  pointer-events: none;
}

.service-card:hover .card-gradient,
.service-card.elevated .card-gradient {
  opacity: 1;
}

.icon-btn {
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: visible !important;
  width: 56px !important;
  height: 56px !important;
}

.icon-btn .icon-display {
  position: relative;
  color: white !important;
  font-size: 1.4rem;
}

.icon-btn i { color: #ffffff !important; position: relative; z-index: 10; }
.icon-btn i::before { color: #ffffff !important; }

.service-card:hover .icon-btn,
.service-card.elevated .icon-btn {
  transform: scale(1.1) rotate(5deg) !important;
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.icon-pulse { animation: iconPulse 0.6s ease-out; }

.menu-btn {
  transition: all 0.3s !important;
  opacity: 0.5;
}

.service-card:hover .menu-btn {
  opacity: 1;
}

.card-title-area {
  padding: 16px 20px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-content {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 17px;
  line-height: 1.3;
  color: #1e293b;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.card-description {
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
  flex: 1;
  margin-bottom: 12px;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-top: auto;
}

.card-link {
  font-size: 13px;
  font-weight: 700;
  color: #1565C0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: gap 0.3s;
}

.service-card:hover .card-link {
  gap: 10px;
}

.card-arrow-icon {
  font-size: 14px !important;
  transition: transform 0.3s;
}

.service-card:hover .card-arrow-icon {
  transform: translateX(4px);
}

.card-badge {
  font-weight: 700;
  font-size: 10px;
}

.menu-list {
  border-radius: 12px !important;
  box-shadow: 0 10px 30px rgba(0,0,0,0.12) !important;
  padding: 8px !important;
}

.menu-item {
  transition: all 0.2s;
  border-radius: 8px;
  margin-bottom: 2px;
}

.menu-item:hover {
  background: #eff6ff !important;
  color: #2563eb !important;
}

/* ===== BACK TO TOP ===== */
.back-to-top {
  margin: 0 24px 24px 0 !important;
  box-shadow: 0 4px 16px rgba(13,71,161,0.3) !important;
  transition: all 0.3s !important;
}

.back-to-top:hover {
  transform: translateY(-4px);
}

/* ===== ANIMATIONS ===== */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideInDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes toastSlideIn {
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
}

@keyframes wave {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(15deg); }
  75% { transform: rotate(-10deg); }
}

@keyframes creditPulse {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
}

@keyframes floatParticle {
  0% { transform: translateY(0) scale(1); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100px) scale(0.5); opacity: 0; }
}

@keyframes shine {
  from { left: -100%; }
  to { left: 200%; }
}

@keyframes iconPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1.1); }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ===== RESPONSIVE ===== */
@media (max-width: 599px) {
  .hero-section {
    padding: 16px 0 28px;
  }

  .welcome-badge, .credit-score-badge {
    font-size: 11px;
    padding: 6px 10px;
    max-width: 95%;
    margin-bottom: 8px;
  }

  .stats-summary {
    flex-direction: row;
    gap: 6px;
    margin-bottom: 12px;
  }

  .stat-item {
    min-width: auto;
    flex: 1;
    padding: 8px 10px;
  }

  .stat-label { font-size: 9px; }
  .stat-value { font-size: 13px; }

  .referral-share {
    padding: 12px;
    margin: 0 8px;
    border-radius: 14px;
  }

  .share-pill { padding: 7px 10px; }
  .share-pill .pill-label { display: none; }

  .main-content { margin-top: -40px; }

  .section-title { font-size: 18px; }
  .service-card { margin-bottom: 8px; }
  .icon-btn { width: 48px !important; height: 48px !important; }
  .icon-btn i { font-size: 1.2rem !important; }
  .card-title { font-size: 15px !important; }
  .card-description { font-size: 12px !important; }

  .toast-container { right: 12px; top: 12px; }
  .toast-item { min-width: auto; width: calc(100vw - 24px); }
}

@media (min-width: 600px) {
  .share-pill .pill-label { display: inline; }
}

@media (min-width: 600px) and (max-width: 959px) {
  .hero-section {
    padding: 24px 0 40px;
  }
}

/* ═══════════════════════════════════════════════════════════
   CREDIT DIALOG — Redesigné
   ═══════════════════════════════════════════════════════════ */
.credit-dialog { border-radius: 20px; }

.glass-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
}

.dialog-header {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.close-btn {
  transition: all 0.3s !important;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Score Circle */
.score-circle-container {
  display: flex;
  justify-content: center;
}

.score-circle {
  position: relative;
  width: 140px;
  height: 140px;
}

.score-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.score-track {
  fill: none;
  stroke: #e3f2fd;
  stroke-width: 8;
}

.score-progress {
  fill: none;
  stroke: url(#scoreGradient);
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 339.292;
  transition: stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.score-value {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.score-number {
  font-size: 36px;
  font-weight: 800;
  color: #1565c0;
  font-family: 'Poppins', sans-serif;
}

.score-label {
  font-size: 14px;
  color: #90a4ae;
  display: block;
}

.section-heading {
  font-size: 18px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
}

.emoji-icon { font-size: 22px; }

.info-alert {
  border-radius: 12px !important;
}

.accumulation-card {
  border-radius: 12px !important;
  overflow: hidden;
  transition: transform 0.3s;
}

.accumulation-card:hover {
  transform: translateX(4px);
}

.point-item {
  transition: background 0.2s;
}

.point-item:hover {
  background: rgba(0, 0, 0, 0.02);
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.benefit-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 12px;
  border-left: 4px solid #2196F3;
  animation: fadeInUp 0.5s ease-out both;
}

.dialog-actions {
  background: linear-gradient(90deg, #f5f5f5, #e3f2fd);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.got-it-btn {
  background: linear-gradient(135deg, #1565c0, #0d47a1) !important;
  box-shadow: 0 4px 15px rgba(21, 101, 192, 0.4) !important;
  transition: all 0.3s !important;
}

.got-it-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.5) !important;
}

</style>